import { useMemo } from 'react'
import { useTheme } from '@mui/material'
import { generateShades } from '~/styles/palette'

/**
 * Predefined color palette for phase types
 * Based on crop_phase_types seed data
 *
 * Phase types (10 total):
 * - stratification: Cold treatment for dormancy breaking
 * - germination: Initial seed sprouting
 * - seedling: Early growth stage
 * - rooting: Root development from cuttings
 * - vegetative: Leaf and stem growth
 * - budding: Bud formation before flowering
 * - flowering: Pollination stage
 * - ripening: Fruit production (not harvest)
 * - curing: Post-harvest drying/aging
 */
const CROP_LIFECYCLE_COLORS = {
  stratification: { ...generateShades('#90A4AE') },
  germination: { ...generateShades('#8D6E63') },
  seedling: { ...generateShades('#AEEA00') },
  rooting: { ...generateShades('#8D6E63') }, // same as rooting, was previously #4DB6AC
  maturing: { ...generateShades('#4CAF50') },
  vegetative: { ...generateShades('#4CAF50') },
  budding: { ...generateShades('#81C784') },
  flowering: { ...generateShades('#F06292') },
  ripening: { ...generateShades('#FFB300') },
  mature: { ...generateShades('#FFC107') },  // Golden/amber for harvest window
  curing: { ...generateShades('#E67E22') },
  decomposition: { ...generateShades('#5D4037') },
  dormant: { ...generateShades('#90A4A0') }, // Muted, pale blue-green-grey for dormancy
}

const SEASON_COLORS = {
  winter: { ...generateShades('#90a7c3') },
  spring: { ...generateShades('#b6c1ac') },
  summer: { ...generateShades('#cfc7a2') },
  fall: { ...generateShades('#cfa69e') },
}

const TICKER_COLORS_BASE = {
  frost: { ...generateShades('#576c95') },
}

const REPORT_COLORS = {
  lfd:         { dark: '#90CAF9', light: '#1565C0' }, // blue
  ffd:         { dark: '#90CAF9', light: '#1565C0' }, // blue
  heat:        { dark: '#FFAB91', light: '#BF360C' }, // deep orange
  drought:     { dark: '#FFE082', light: '#E65100' }, // amber/orange
  flood:       { dark: '#80DEEA', light: '#006064' }, // cyan
  wind:        { dark: '#80CBC4', light: '#004D40' }, // teal
  catastrophe: { dark: '#EF9A9A', light: '#B71C1C' }, // red
  other:       { dark: '#B0BEC5', light: '#455A64' }, // blue grey
}

/**
 * Task color palette (distinct from phases)
 * Based on task_types seed data
 *
 * Task types (9 total):
 * - plot_summer_prep: Soil preparation for growing season
 * - sow_indoor: Sow seeds indoors
 * - transplant_indoor: Indoor transplant
 * - transplant_outdoor: Outdoor transplant
 * - sow_outdoor: Sow seeds directly outdoors
 * - support: Support/trellis installation
 * - harvest: Collect mature crops
 * - compost: Compost/breakdown biomass
 * - plot_winter_prep: Soil winter preparation
 */
export const TASK_TYPE_ICONS = {
  plot_summer_prep: "🌱",
  sow_indoor: "🌿",
  transplant_indoor: "🪴",
  transplant_outdoor: "🌻",
  sow_outdoor: "🌾",
  support: "🎋",
  harvest: "🧺",
  compost: "♻️",
  cut_back: "✂️",
  plot_winter_prep: "❄️",
}

const TASK_COLORS = {
  plot_summer_prep: { ...generateShades('#7E57C2') },  // Purple
  sow_indoor: { ...generateShades('#5C6BC0') },        // Indigo
  transplant_indoor: { ...generateShades('#42A5F5') }, // Blue
  transplant_outdoor: { ...generateShades('#26A69A') },// Teal
  sow_outdoor: { ...generateShades('#66BB6A') },       // Green
  support: { ...generateShades('#9CCC65') },           // Light Green
  harvest: { ...generateShades('#FFB300') },           // Darker Amber
  compost: { ...generateShades('#8D6E63') },           // Brown
  cut_back: { ...generateShades('#90A4A0') },          // 
  plot_winter_prep: { ...generateShades('#78909C') },  // Blue Grey
}

const REPORT_ICONS = {
  catastrophe: '💥',
  drought: '🌵',
  ffd: '❄️',
  flood: '🌊',
  heat: '🌡️',
  lfd: '❄️',
  other: '📝',
  wind: '💨',
}

/**
 * Hook that provides theme-aware timeline colors
 * Recomputes when theme mode changes (light/dark)
 */
export const useTimelineStyles = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  return useMemo(() => {
    const TICKER_COLORS = {
      now: theme.palette.primary,
      hover: theme.palette.primary,
      frost: TICKER_COLORS_BASE.frost,
    }

    const WEEK_TINT_COLOR = isDark
      ? theme.palette.primary[600]
      : theme.palette.primary[200]

    const ROW_SEPARATOR_COLOR = isDark
      ? theme.palette.grey[700]
      : theme.palette.primary[100]

    const PHASE_BORDER_COLOR = isDark
      ? theme.palette.grey[100]
      : theme.palette.grey[900]

    const PhaseStyles = {
      stratification: {
        icon: "❄️",
        color: isDark ? CROP_LIFECYCLE_COLORS.stratification[900] : CROP_LIFECYCLE_COLORS.stratification.light,
        label: "Stratification"
      },
      germination: {
        icon: "🌱",
        color: isDark ? CROP_LIFECYCLE_COLORS.germination[900] : CROP_LIFECYCLE_COLORS.germination.light,
        label: "Germination"
      },
      seedling: {
        icon: "🌿",
        color: isDark ? CROP_LIFECYCLE_COLORS.seedling[900] : CROP_LIFECYCLE_COLORS.seedling.light,
        label: "Seedling"
      },
      rooting: {
        icon: "🪱",
        color: isDark ? CROP_LIFECYCLE_COLORS.rooting[900] : CROP_LIFECYCLE_COLORS.rooting.light,
        label: "Rooting"
      },
      maturing: {
        icon: "🌾",
        color: isDark ? CROP_LIFECYCLE_COLORS.maturing[900] : CROP_LIFECYCLE_COLORS.maturing.light,
        label: "Maturing"
      },
      vegetative: {
        icon: "🥬",
        color: isDark ? CROP_LIFECYCLE_COLORS.vegetative[900] : CROP_LIFECYCLE_COLORS.vegetative.light,
        label: "Vegetative"
      },
      budding: {
        icon: "🌸",
        color: isDark ? CROP_LIFECYCLE_COLORS.budding[900] : CROP_LIFECYCLE_COLORS.budding.light,
        label: "Budding"
      },
      flowering: {
        icon: "🌼",
        color: isDark ? CROP_LIFECYCLE_COLORS.flowering[900] : CROP_LIFECYCLE_COLORS.flowering.light,
        label: "Flowering"
      },
      ripening: {
        icon: "🍅",
        color: isDark ? CROP_LIFECYCLE_COLORS.ripening[900] : CROP_LIFECYCLE_COLORS.ripening.light,
        label: "Ripening"
      },
      curing: {
        icon: "🥔",
        color: isDark ? CROP_LIFECYCLE_COLORS.curing[900] : CROP_LIFECYCLE_COLORS.curing.light,
        label: "Curing"
      },
      mature: {
        icon: "🌻",
        color: isDark ? CROP_LIFECYCLE_COLORS.mature[900] : CROP_LIFECYCLE_COLORS.mature.light,
        label: "Mature"
      },
      decomposition: {
        icon: "🍂",
        color: isDark ? CROP_LIFECYCLE_COLORS.decomposition[900] : CROP_LIFECYCLE_COLORS.decomposition.light,
        label: "Decomposition"
      },
      dormant: {
        icon: "🛌",
        color: isDark ? CROP_LIFECYCLE_COLORS.dormant[900] : CROP_LIFECYCLE_COLORS.dormant.light,
        label: "Dormant"
      },
    }

    const TaskStyles = {
      plot_summer_prep: {
        icon: "🌱",
        color: isDark ? TASK_COLORS.plot_summer_prep.main : TASK_COLORS.plot_summer_prep[400],
        label: "Plot Prep"
      },
      sow_indoor: {
        icon: "🌿",
        color: isDark ? TASK_COLORS.sow_indoor.main : TASK_COLORS.sow_indoor[400],
        label: "Sow Indoor"
      },
      transplant_indoor: {
        icon: "🪴",
        color: isDark ? TASK_COLORS.transplant_indoor.main : TASK_COLORS.transplant_indoor[400],
        label: "Indoor Transplant"
      },
      transplant_outdoor: {
        icon: "🌻",
        color: isDark ? TASK_COLORS.transplant_outdoor.main : TASK_COLORS.transplant_outdoor[400],
        label: "Outdoor Transplant"
      },
      sow_outdoor: {
        icon: "🌾",
        color: isDark ? TASK_COLORS.sow_outdoor.main : TASK_COLORS.sow_outdoor[400],
        label: "Sow Outdoor"
      },
      support: {
        icon: "🎋",
        color: isDark ? TASK_COLORS.support.main : TASK_COLORS.support[400],
        label: "Support/Trellis"
      },
      harvest: {
        icon: "🧺",
        color: isDark ? TASK_COLORS.harvest.main : TASK_COLORS.harvest[400],
        label: "Harvest"
      },
      compost: {
        icon: "♻️",
        color: isDark ? TASK_COLORS.compost.main : TASK_COLORS.compost[400],
        label: "Compost"
      },
      cut_back: {
        icon: "✂️",
        color: isDark ? TASK_COLORS.cut_back.main : TASK_COLORS.cut_back[400],
        label: "Cut Back"
      },
      plot_winter_prep: {
        icon: "❄️",
        color: isDark ? TASK_COLORS.plot_winter_prep.main : TASK_COLORS.plot_winter_prep[400],
        label: "Winter Prep"
      },
    }

    const SeasonStyles = [
      {
        name: 'Winter',
        startWeek: 0,
        endWeek: 11,
        color: isDark ? SEASON_COLORS.winter.dark : SEASON_COLORS.winter.light,
      },
      {
        name: 'Spring',
        startWeek: 11,
        endWeek: 24,
        color: isDark ? SEASON_COLORS.spring.dark : SEASON_COLORS.spring.light,
      },
      {
        name: 'Summer',
        startWeek: 24,
        endWeek: 37,
        color: isDark ? SEASON_COLORS.summer.dark : SEASON_COLORS.summer.light,
      },
      {
        name: 'Fall',
        startWeek: 37,
        endWeek: 50,
        color: isDark ? SEASON_COLORS.fall.dark : SEASON_COLORS.fall.light,
      },
      {
        name: 'Winter ',
        startWeek: 50,
        endWeek: 52,
        color: isDark ? SEASON_COLORS.winter.dark : SEASON_COLORS.winter.light,
      },
    ]

    const TimelineStyles = {
      now: {
        fillColor: isDark ? TICKER_COLORS.now[400] : TICKER_COLORS.now.light,
        textColor: isDark ? '#000' : '#fff',
        width: 3,
      },
      hover: {
        fillColor: isDark ? TICKER_COLORS.hover[300] : TICKER_COLORS.hover[500],
        textColor: isDark ? '#000' : '#fff',
        width: 2,
      },
      frost: {
        color: isDark ? TICKER_COLORS.frost.light : TICKER_COLORS.frost.main,
        textColor: '#fff',
      },
      report: (code) => {
        // the frost estimates are packaged into reports as well, since they're estimated reports
        const entry = REPORT_COLORS[code] || TICKER_COLORS.frost
        return {
          color: isDark ? entry.dark : entry.light,
          icon: REPORT_ICONS[code],
        }
      },
    }

    const TaskOverlayStyles = {
      cutoffLine: isDark ? TICKER_COLORS.hover[300] : TICKER_COLORS.hover[500],
      mask: isDark ? 'rgba(32, 32, 32, 0.5)' : 'rgba(128, 128, 128, 0.35)',
    }

    /**
     * Get color for phase type with fallback
     * @param {string} phaseCode - Phase type code (e.g., 'germination', 'vegetative')
     * @returns {string} Hex color code
     */
    const getPhaseColor = (phaseCode) => {
      return CROP_LIFECYCLE_COLORS[phaseCode] || '#9E9E9E'
    }

    /**
     * Get phase style with fallback
     * @param {string} phaseCode - Phase type code
     * @returns {{ icon: string, color: string, label: string }}
     */
    const getPhaseStyle = (phaseCode) => {
      return PhaseStyles[phaseCode] || { icon: "📅", color: "#9E9E9E", label: phaseCode }
    }

    /**
     * Get color for task type with fallback
     * @param {string} taskCode - Task type code (e.g., 'sow_indoor', 'harvest')
     * @returns {string} Hex color code
     */
    const getTaskColor = (taskCode) => {
      return TASK_COLORS[taskCode] || '#9E9E9E'
    }

    /**
     * Get task style with fallback
     * @param {string} taskCode - Task type code
     * @returns {{ icon: string, color: string, label: string }}
     */
    const getTaskStyle = (taskCode) => {
      return TaskStyles[taskCode] || { icon: "📌", color: theme.palette.grey[500], label: taskCode }
    }

    return {
      TICKER_COLORS,
      WEEK_TINT_COLOR,
      ROW_SEPARATOR_COLOR,
      PHASE_BORDER_COLOR,
      PhaseStyles,
      TaskStyles,
      SeasonStyles,
      TimelineStyles,
      TaskOverlayStyles,
      getPhaseColor,
      getPhaseStyle,
      getTaskColor,
      getTaskStyle,
    }
  }, [isDark, theme.palette])
}
